import React, { useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import GuideInfoBox from 'pages/guide/components/GuideInfoBox';
import GuidePageLayout from 'pages/guide/components/GuidePageLayout';
import GuideSection from 'pages/guide/components/GuideSection';
import GuideStepList from 'pages/guide/components/GuideStepList';
import GuideTable from 'pages/guide/components/GuideTable';

const INSTANCE_ID_PLACEHOLDER = '<instance-id>';

const CodeBlock: React.FC<{ children: string; instanceId: string }> = ({
  children,
  instanceId,
}) => {
  const [copied, setCopied] = useState(false);

  const displayText = instanceId
    ? children.replace(
        new RegExp(INSTANCE_ID_PLACEHOLDER.replace(/[<>-]/g, '\\$&'), 'g'),
        instanceId,
      )
    : children;

  const handleCopy = () => {
    navigator.clipboard.writeText(displayText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component="pre"
        sx={{
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          pr: 5,
          overflow: 'auto',
          fontSize: '0.8rem',
          lineHeight: 1.6,
          fontFamily: 'monospace',
        }}
      >
        <code>{displayText}</code>
      </Box>
      <Tooltip title={copied ? 'Copied!' : 'コピー'}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: copied ? 'success.main' : 'text.secondary',
            '&:hover': { bgcolor: 'action.selected' },
          }}
        >
          {copied ? <DoneIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const GuideDeveloperDbAccess: React.FC = () => {
  const [instanceId, setInstanceId] = useState('');

  return (
    <GuidePageLayout
      title="踏み台サーバー経由のDB接続手順"
      description="AWS SSM を使用して踏み台サーバー（Bastion）経由で本番データベースに接続する手順です。"
      breadcrumbs={[{ label: 'デプロイ', path: '/guide/deploy/overview' }]}
    >
      <GuideInfoBox type="warning">
        本手順はDEVELOPER権限を持つユーザーのみ実行できます。DBのパスワードは本ガイドに記載していません。別途管理されているドキュメントを参照してください。
      </GuideInfoBox>

      <GuideSection title="前提条件">
        <Stack spacing={1.5}>
          <GuideTable
            headers={['項目', '要件']}
            rows={[
              ['AWS CLI', 'v2 がインストール済み'],
              ['Session Manager Plugin', 'AWS SSM Session Manager プラグインがインストール済み'],
              ['AWS認証', 'ap-northeast-1 リージョンで認証済み'],
              [
                'mysql クライアント（方法Bの場合）',
                'ローカルに mysql-cli または DBeaver 等のGUIツール',
              ],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="接続情報">
        <Stack spacing={1.5}>
          <GuideTable
            headers={['項目', '値']}
            rows={[
              ['CloudFormation スタック名', 'shared-prod-bastion'],
              ['RDS ホスト', 'kanzhong-prod-db.ctggymu083ed.ap-northeast-1.rds.amazonaws.com'],
              ['RDS ポート', '3306'],
              ['DB ユーザー', 'kanzhong_admin'],
              ['DB パスワード', '※ 別途管理ドキュメント参照'],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="踏み台インスタンスIDの取得">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            踏み台サーバーのインスタンスIDを CloudFormation の出力から取得します。
          </Typography>
          <CodeBlock
            instanceId={instanceId}
          >{`aws cloudformation describe-stacks --stack-name shared-prod-bastion \\
  --query 'Stacks[0].Outputs[?OutputKey==\`BastionInstanceId\`].OutputValue' --output text`}</CodeBlock>
          <GuideInfoBox>
            取得したインスタンスIDを下のフィールドに入力すると、以降のコマンドに自動反映されます。
          </GuideInfoBox>
          <TextField
            label="インスタンスID"
            placeholder="i-0123456789abcdef0"
            value={instanceId}
            onChange={(e) => setInstanceId(e.target.value)}
            size="small"
            fullWidth
            sx={{ maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="踏み台の起動">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            踏み台サーバーは通常停止状態です。使用する前に起動してください。
          </Typography>
          <CodeBlock
            instanceId={instanceId}
          >{`aws ec2 start-instances --instance-ids <instance-id>`}</CodeBlock>
          <GuideInfoBox>インスタンスが running 状態になるまで1〜2分程度かかります。</GuideInfoBox>
        </Stack>
      </GuideSection>

      <GuideSection title="方法A: 踏み台にSSMで接続してmysqlを実行（推奨）">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            ローカルに mysql クライアントが不要です。踏み台にインストール済みの mysql
            クライアントを使用します。
          </Typography>
          <GuideStepList
            steps={[
              {
                label: '踏み台にシェル接続',
                content: (
                  <CodeBlock
                    instanceId={instanceId}
                  >{`aws ssm start-session --target <instance-id>`}</CodeBlock>
                ),
              },
              {
                label: '踏み台内で RDS に接続',
                description: 'パスワードを求められたら、別途管理されているパスワードを入力します。',
                content: (
                  <CodeBlock
                    instanceId={instanceId}
                  >{`mysql -h kanzhong-prod-db.ctggymu083ed.ap-northeast-1.rds.amazonaws.com -P 3306 -u kanzhong_admin -p`}</CodeBlock>
                ),
              },
              {
                label: 'SQL を実行',
                description: '接続が完了すると mysql プロンプトが表示されます。',
                content: (
                  <CodeBlock
                    instanceId={instanceId}
                  >{`SHOW DATABASES;\nUSE kanzhong;\nSHOW TABLES;`}</CodeBlock>
                ),
              },
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="方法B: SSMポートフォワーディング（ローカルのGUIツールで接続）">
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            ローカルの DBeaver 等の GUI ツールから RDS に接続したい場合は、SSM
            ポートフォワーディングを使用します。
          </Typography>
          <GuideStepList
            steps={[
              {
                label: 'ポートフォワーディングを開始',
                description:
                  'ローカルの 13306 ポートを RDS の 3306 ポートに転送します。このターミナルは接続中ずっと開いたままにしてください。',
                content: (
                  <CodeBlock instanceId={instanceId}>{`aws ssm start-session \\
  --target <instance-id> \\
  --document-name AWS-StartPortForwardingSessionToRemoteHost \\
  --parameters '{"host":["kanzhong-prod-db.ctggymu083ed.ap-northeast-1.rds.amazonaws.com"],"portNumber":["3306"],"localPortNumber":["13306"]}'`}</CodeBlock>
                ),
              },
              {
                label: '別ターミナルで接続',
                description:
                  'mysql-cli の場合は以下のコマンドで接続します。GUIツールの場合は localhost:13306 を接続先として設定してください。',
                content: (
                  <CodeBlock
                    instanceId={instanceId}
                  >{`mysql -h 127.0.0.1 -P 13306 -u kanzhong_admin -p`}</CodeBlock>
                ),
              },
            ]}
          />
          <GuideTable
            headers={['GUIツールの設定項目', '値']}
            rows={[
              ['ホスト', '127.0.0.1'],
              ['ポート', '13306'],
              ['ユーザー', 'kanzhong_admin'],
              ['パスワード', '※ 別途管理ドキュメント参照'],
              ['データベース', 'kanzhong'],
            ]}
          />
        </Stack>
      </GuideSection>

      <GuideSection title="作業後: 踏み台の停止">
        <Stack spacing={1.5}>
          <GuideInfoBox type="warning">
            作業が終わったら必ず踏み台サーバーを停止してください。起動したまま放置するとコストが発生します。
          </GuideInfoBox>
          <CodeBlock
            instanceId={instanceId}
          >{`aws ec2 stop-instances --instance-ids <instance-id>`}</CodeBlock>
          <Typography variant="body2" color="text.secondary">
            停止の確認:
          </Typography>
          <CodeBlock
            instanceId={instanceId}
          >{`aws ec2 describe-instances --instance-ids <instance-id> \\
  --query 'Reservations[0].Instances[0].State.Name' --output text`}</CodeBlock>
          <GuideInfoBox>&quot;stopping&quot; → &quot;stopped&quot; になれば完了です。</GuideInfoBox>
        </Stack>
      </GuideSection>

      <GuideSection title="トラブルシューティング">
        <Stack spacing={1.5}>
          <GuideTable
            headers={['症状', '原因', '対処']}
            rows={[
              [
                'SSM接続がタイムアウトする',
                'インスタンスが stopped 状態',
                'aws ec2 start-instances で起動してから再試行',
              ],
              [
                'TargetNotConnected エラー',
                'SSM Agent が起動していない',
                'インスタンスを再起動（stop → start）して数分待つ',
              ],
              [
                'ポートフォワーディングが失敗する',
                'Session Manager プラグイン未インストール',
                'AWS公式ドキュメントからプラグインをインストール',
              ],
              [
                'mysql接続でAccess Denied',
                'パスワードが間違っている',
                '管理ドキュメントで最新のパスワードを確認',
              ],
              [
                'ローカルポート 13306 が使用中',
                '前回のセッションが残っている',
                '前回のSSMセッションを終了するか、別のポート番号を使用',
              ],
            ]}
          />
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideDeveloperDbAccess;
